/**
 * Saw — a circular-blade hazard (Brief 012 Phase 1 + track-saw in Phase 2).
 *
 * Always-hurt like SPIKE (costs a heart; respects i-frames). Three flavours, all one
 * class:
 *   • static  — sits in place.
 *   • spinning — `props.spin` (default true) swaps the blade sprite frame for a spin
 *     LOOK. The spin is VISUAL ONLY: collision stays the blade's AABB (the brief's
 *     "spin sprite is visual-only, no collision rotation" rule), so no trig is needed.
 *   • track    — `props.path: true` rides a deterministic ping-pong path along one axis
 *     (`axis`/`range`/`speed`), reusing the Brief 011 kinematic-path motion model.
 *
 * Determinism: the spin frame is an integer step counter; the track is a fixed-step
 * parametric offset reflected at the ends (linear ping-pong — transcendental-free, the
 * same construction as Platform). No Math.sin/cos, no wall-clock, no RNG. ESLint-clean.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

/** Hitbox inset from the 64² cell — a saw's danger arc is a touch smaller than its art. */
const HALF = 26;
/** Fixed steps each blade frame shows before the swap (~0.1s @ 60Hz → a brisk spin). */
const SPIN_FRAME_STEPS = 6;
const DEFAULT_RANGE_TILES = 3;
const DEFAULT_TRACK_SPEED = 120; // px/s

function numProp(props: Record<string, unknown> | undefined, key: string, fallback: number): number {
  const v = props?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

export class Saw implements Entity {
  readonly type = EntityType.SAW;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private readonly spin: boolean;
  private frame = 0;

  // Track-saw motion (Brief 011 ping-pong; inert unless props.path).
  private readonly moving: boolean;
  private readonly axis: 'x' | 'y';
  private readonly span: number;
  private readonly speed: number;
  private readonly originX: number;
  private readonly originY: number;
  private offset = 0;
  private dir: 1 | -1 = 1;

  /**
   * @param cx    placed-cell center-x, @param feetY placed-cell bottom.
   * @param props `spin` (def true), `path` (def false → static), `axis` ('x'|'y'),
   *              `range` (tiles), `speed` (px/s) for a track saw.
   * @param ts    tile size (px) — the blade is centered in its cell.
   */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    const centerY = feetY - ts / 2; // vertically centered in the cell
    this.body = { x: cx - HALF, y: centerY - HALF, w: HALF * 2, h: HALF * 2 };
    this.spin = props?.spin !== false;
    this.moving = props?.path === true;
    this.axis = props?.axis === 'y' ? 'y' : 'x';
    this.span = Math.abs(numProp(props, 'range', DEFAULT_RANGE_TILES)) * ts;
    this.speed = Math.max(0, numProp(props, 'speed', DEFAULT_TRACK_SPEED));
    this.originX = this.body.x;
    this.originY = this.body.y;
  }

  update(dt: number, _ctx: EntityContext): void {
    if (this.spin) this.frame++;
    if (this.moving && this.span > 0 && this.speed > 0) {
      this.offset += this.dir * this.speed * dt;
      if (this.offset > this.span) { this.offset = 2 * this.span - this.offset; this.dir = -1; }
      else if (this.offset < 0) { this.offset = -this.offset; this.dir = 1; }
      this.offset = Math.max(0, Math.min(this.span, this.offset)); // clamp for extreme params
      if (this.axis === 'x') {
        this.body.x = this.originX + this.offset;
        this.facing = this.dir; // flip the blade with travel direction (cosmetic)
      } else {
        this.body.y = this.originY + this.offset;
      }
    }
  }

  onPlayerOverlap(ctx: EntityContext): void {
    ctx.hurtPlayer(); // always-hurt; never stompable (like SPIKE)
  }

  spriteKey(): string {
    // Two-frame blade spin (visual only). Static saw (spin off) shows frame 0.
    return this.spin && Math.floor(this.frame / SPIN_FRAME_STEPS) % 2 === 1 ? 'saw_spin' : 'saw';
  }
  visible(): boolean { return true; }
}
